import "./App.css";
import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import usePageMeta from "./seo/usePageMeta";
import Home from "./pages/Home";
import Projects from "./pages/Projects";
import Experience from "./pages/Experience";
import ContactForm from "./pages/ContactForm";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import ProjectDisplay from "./pages/ProjectDisplay";
import { Analytics } from "@vercel/analytics/react";

// Loaded on demand so the search page's code never slows down the rest of the site.
const HealthSearch = lazy(() => import("./healthsearch/HealthSearch"));
const HealthSearchMethodology = lazy(() => import("./healthsearch/Methodology"));
const HealthSearchPriceChanges = lazy(() => import("./healthsearch/PriceChanges"));

// Updates the tab title, description and share tags for the current page.
function PageMeta() {
  const { pathname } = useLocation();
  usePageMeta(pathname.length > 1 ? pathname.replace(/\/$/, "") : pathname);
  return null;
}

function App() {
  const [firstTime, setFirstTime] = useState(localStorage.getItem("firstTime") || true);
  const [darkMode, setDarkMode] = useState(JSON.parse(localStorage.getItem("darkMode")) || false);

  if (firstTime === true) {
    localStorage.setItem("firstTime", false);
    setFirstTime(false);
  }

  // keep the color-scheme dark when you refresh
  useEffect(() => {
    if (darkMode === true) {
      document.documentElement.style.setProperty('color-scheme', 'dark');
    }
    else {
      document.documentElement.style.setProperty('color-scheme', 'light');
    }
  }, [darkMode])

  function toggleTheme() {
    localStorage.setItem("darkMode", !darkMode);
    setDarkMode(!darkMode);
  } 

  return (
    <div className="App">
      <div className={(darkMode ? "dark" : "") + " flex flex-col min-h-screen"}>
        <Router>
          <PageMeta />
          <ScrollToTop />
          <Navbar toggleTheme={toggleTheme} darkMode={darkMode}/>
          <div className="content flex-grow">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/:id" element={<ProjectDisplay />} />
              <Route path="/experience" element={<Experience />} />
              <Route path="/contact" element={<ContactForm />} />
              <Route
                path="/healthsearch"
                element={
                  <Suspense fallback={<div className="flex-grow" />}>
                    <HealthSearch />
                  </Suspense>
                }
              />
              <Route
                path="/healthsearch/methodology"
                element={
                  <Suspense fallback={<div className="flex-grow" />}>
                    <HealthSearchMethodology />
                  </Suspense>
                }
              />
              {["/healthsearch/price-changes", "/healthsearch/price-changes/:month"].map((path) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <Suspense fallback={<div className="flex-grow" />}>
                      <HealthSearchPriceChanges />
                    </Suspense>
                  }
                />
              ))}
            </Routes>
          </div>
          <Footer />
        </Router>
        {/* Vercel Web Analytics: cookieless page-view counts, visible in the Vercel dashboard. */}
        <Analytics />
      </div>
    </div>
  );
}

export default App;
